import { Slot as SlotPrimitive } from 'radix-ui';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMedia } from 'react-use';

import { Button } from './Button';
import { Icon } from './Icon';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './Sheet';
import { Stack } from './Stack';
import { VariantProps, tv } from './utils/cn';

const Slot = SlotPrimitive.Slot;

type DetailsSidebarContext = {
  alwaysOverlay: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  variant: 'fixed' | 'overlay';
  isFullscreen: boolean;
  setFullscreen: (fullscreen: boolean) => void;
  toggleFullscreen: () => void;
};

const DetailsSidebarContext = createContext<DetailsSidebarContext | null>(null);

function useDetailsSidebar() {
  const context = useContext(DetailsSidebarContext);
  if (!context) {
    throw new Error(
      'useDetailsSidebar must be used within a DetailsSidebarProvider.',
    );
  }

  return context;
}

function DetailsSidebarProvider({
  children,
  overlayOnly = false,
  onOpenChange,
  onFullscreenChange,
}: {
  children: ReactNode;
  overlayOnly?: boolean;
  onOpenChange?: (open: boolean) => void;
  onFullscreenChange?: (fullscreen: boolean) => void;
}) {
  const [open, setOpenState] = useState(false);
  const [isFullscreen, setFullscreenState] = useState(false);

  const setOpen = useCallback(
    (next: boolean) => {
      if (next !== open) onOpenChange?.(next);
      setOpenState(next);
    },
    [open, onOpenChange],
  );

  const setFullscreen = useCallback(
    (next: boolean) => {
      if (next !== isFullscreen) onFullscreenChange?.(next);
      setFullscreenState(next);
    },
    [isFullscreen, onFullscreenChange],
  );

  const toggleSidebar = useCallback(() => {
    const next = !open;
    setOpenState(next);
    onOpenChange?.(next);
  }, [open, onOpenChange]);

  const toggleFullscreen = useCallback(() => {
    const next = !isFullscreen;
    setFullscreenState(next);
    onFullscreenChange?.(next);
  }, [isFullscreen, onFullscreenChange]);

  const canBeFixed = useMedia('(min-width: 64rem)');
  const variant = overlayOnly || !canBeFixed ? 'overlay' : 'fixed';

  useEffect(() => {
    if (variant === 'fixed' && open) {
      setOpen(false);
    }
  }, [variant, open, setOpen]);

  const contextValue = useMemo<DetailsSidebarContext>(
    () => ({
      alwaysOverlay: overlayOnly,
      open,
      setOpen,
      toggleSidebar,
      variant,
      isFullscreen,
      setFullscreen,
      toggleFullscreen,
    }),
    [
      overlayOnly,
      open,
      setOpen,
      toggleSidebar,
      variant,
      isFullscreen,
      setFullscreen,
      toggleFullscreen,
    ],
  );

  return (
    <DetailsSidebarContext.Provider value={contextValue}>
      {children}
    </DetailsSidebarContext.Provider>
  );
}

function DetailsSidebarTrigger({ children }: { children: ReactNode }) {
  const { toggleSidebar, variant } = useDetailsSidebar();

  if (variant === 'fixed') {
    return null;
  }

  return <Slot onClick={() => toggleSidebar()}>{children}</Slot>;
}

/** Width of the fixed (inline rail) variant; overlay width is `size`. */
const fixedSidebarStyles = tv({
  base: 'col-start-2 row-span-2 row-start-2 h-full self-stretch overflow-auto border-l border-border-primary',
  variants: {
    fixedWidth: {
      sm: 'w-80',
      md: 'w-96',
    },
  },
  defaultVariants: {
    fixedWidth: 'sm',
  },
});

type FixedSidebarStyleProps = VariantProps<typeof fixedSidebarStyles>;

const detailsSidebarStyles = tv({
  base: '',
  variants: {
    size: {
      sm: 'w-80',
      md: 'w-112',
      lg: 'w-full md:w-144',
    },
    isFullscreen: {
      true: 'w-screen!',
    },
  },
  defaultVariants: {
    size: 'sm',
    isFullscreen: false,
  },
});

type DetailsSidebarStyleProps = VariantProps<typeof detailsSidebarStyles>;

/**
 * A component that renders a sidebar for displaying details or additional functionality.
 *
 * @param children - The content to be displayed inside the sidebar.
 * @param description - The optional subtext displayed below the title when in an overlay.
 * @param size - The size of the sidebar when in an overlay.
 * @param title - The text displayed on top of the sidebar when in an overlay.
 *
 * @remark
 * The sidebar is designed to be used within a `DetailsSidebarProvider` context.
 * The provider has an optional `overlayOnly` prop that determines whether the sidebar should only be displayed in overlay mode.
 * By default it will be presented inline on the right side when the screen is large enough.
 */
function DetailsSidebar({
  children,
  description,
  size,
  fixedWidth,
  title,
  allowFullscreen = false,
}: {
  /**
   * The content to be displayed inside the sidebar.
   */
  children: ReactNode;
  /**
   * The optional subtext displayed below the title when in an overlay.
   */
  description?: string;
  /**
   * The size of the sidebar when in an overlay.
   */
  size?: DetailsSidebarStyleProps['size'];
  /**
   * The width of the sidebar when fixed (inline rail): sm = 320px (default),
   * md = 384px. Overlay width is controlled by `size` instead.
   */
  fixedWidth?: FixedSidebarStyleProps['fixedWidth'];
  /**
   * The text displayed on top of the sidebar when in an overlay.
   */
  title: string;
  /**
   * Whether to allow fullscreen mode for this sidebar.
   */
  allowFullscreen?: boolean;
}) {
  const {
    alwaysOverlay,
    open: isOpen,
    setOpen,
    variant,
    isFullscreen,
    toggleFullscreen,
  } = useDetailsSidebar();

  if (variant === 'fixed') {
    return (
      <aside className={fixedSidebarStyles({ fixedWidth })}>{children}</aside>
    );
  }

  return (
    <Sheet modal={!alwaysOverlay} open={isOpen} onOpenChange={setOpen}>
      <SheetContent
        className={detailsSidebarStyles({ size, isFullscreen })}
        showOverlay={!alwaysOverlay}
      >
        {allowFullscreen && (
          <Button
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            className="absolute top-3 right-11"
            onClick={toggleFullscreen}
            size="sm"
            variant="ghost"
          >
            <Icon name={isFullscreen ? 'Minimize2' : 'Maximize2'} size="auto" />
          </Button>
        )}
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <Stack className="min-h-0 flex-1 overflow-hidden">{children}</Stack>
      </SheetContent>
    </Sheet>
  );
}

export {
  DetailsSidebar,
  DetailsSidebarProvider,
  DetailsSidebarTrigger,
  useDetailsSidebar,
};
